import { useLingui } from "@lingui/react/macro";
import { useRef, useState } from "react";
import useSWRMutation from "swr/mutation";

import { useUser } from "~/ui/provider/Auth.tsx";
import { useNotification } from "~/ui/provider/Notification.tsx";

export interface Form {
  name: string;
  profileImageUrl: null | string;
}

export const FORM_NAME = {
  NAME: "name",
  PROFILE_IMAGE: "profileImageUrl",
};

export const useUpdateMyInfo = (close: () => void) => {
  const formRef = useRef<HTMLFormElement>(null);
  const push = useNotification();
  const { t } = useLingui();

  const { client, myInfo, patchUser } = useUser();
  const [data, setData] = useState<Form>(
    myInfo ?? { name: "", profileImageUrl: null },
  );

  const { isMutating, trigger } = useSWRMutation(
    "util/image",
    async (key, { arg: form }: { arg: HTMLFormElement }) => {
      const formData = new FormData(form);
      const profileImageUrl =
        (data.profileImageUrl !== myInfo?.profileImageUrl
          ? await client.post(key, { body: formData }).text()
          : data.profileImageUrl) ?? undefined;
      await patchUser({
        name: data.name,
        profileImageUrl,
      });
    },
    {
      onError: () => {
        push({ content: t`다시 시도해주세요.` });
      },
      onSuccess: () => {
        close();
      },
    },
  );

  const handleImageChange = (file: File | undefined) => {
    if (!file) return;
    if (file.size > 4_000_000) {
      push({ content: t`더 작은 사진을 사용해주세요.` });
      return;
    }
    const profileImageUrl = URL.createObjectURL(file);
    setData((p) => ({ ...p, profileImageUrl }));
  };

  const handleNameChange = (name: string) => {
    setData((p) => ({ ...p, name }));
  };

  const handleSubmit = () => {
    if (formRef.current) {
      void trigger(formRef.current);
    }
  };

  return {
    data,
    formRef,
    handleImageChange,
    handleNameChange,
    handleSubmit,
    isMutating,
  };
};
